import type { MatrimonyConnection } from "src/types/matrimonyConnections.types";

export type ContactRequestTabKey = "RECEIVED" | "SENT" | "CONNECTIONS";

const computeAge = (isoDob?: string | null): number | undefined => {
  if (!isoDob) return undefined;
  const d = new Date(isoDob);
  if (Number.isNaN(d.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return Math.max(0, age);
};

const toHeightLabel = (cm?: number | null): string | undefined => {
  if (!cm) return undefined;
  return `${cm} cm`;
};

// Map ACCEPTED request DTO into existing MatrimonyConnection UI object.
// Supports both legacy nested shapes and the newer flat ACCEPTED response.
export const mapConnectionDtoToUi = (dto: Record<string, any>): MatrimonyConnection => {
  const sender = (dto as any).senderProfile || (dto as any).sender || {};
  const receiver = (dto as any).receiverProfile || (dto as any).receiver || {};

  // Flat accepted DTO fields (new backend response)
  const flatOther = {
    id: (dto as any)?.otherProfileId ?? (dto as any)?.profileId,
    fullName: (dto as any)?.fullName,
    // keep raw protected filename; UI will convert via fetchAuthedImageObjectUrl
    profilePhotoFileName: (dto as any)?.profileImage,
    mobileNo: (dto as any)?.mobile ?? (dto as any)?.mobileNo,
    email: (dto as any)?.email,
    dateOfBirth: (dto as any)?.dateOfBirth,
    heightInCm: (dto as any)?.heightInCm,
    designation: (dto as any)?.designation,
    employerName: (dto as any)?.employerName,
    employmentSector: (dto as any)?.employmentSector,
  };

  // Legacy nested profile shapes
  const profileRaw =
    (dto as any)?.profile ??
    (dto as any)?.connectedProfile ??
    (dto as any)?.toProfile ??
    (dto as any)?.fromProfile;

  const other = profileRaw || (flatOther.fullName ? flatOther : null) || sender || receiver || {};

  return {
    id:
      (dto as any)?.requestId ??
      (dto as any)?.id ??
      (dto as any)?.connectionId ??
      other?.id ??
      String(Math.random()),
      senderProfileId: (dto as any)?.senderProfileId ?? sender?.id,
      receiverProfileId: (dto as any)?.receiverProfileId ?? receiver?.id,
    profile: {
      id:
        other?.id ??
        (dto as any)?.senderProfileId ??
        (dto as any)?.receiverProfileId ??
        (dto as any)?.id,
      fullName: String(other?.fullName ?? other?.name ?? ""),
      // don't pass protected path directly into Avatar; keep filename for authed fetch
      profilePhoto: other?.profilePhoto ?? other?.photoUrl ?? null,
      // @ts-expect-error - used by UI layer for authed image fetch
      profilePhotoFileName: other?.profilePhotoFileName ?? null,
      mobileNo: other?.mobileNo ?? other?.phone ?? other?.mobileNumber ?? null,
      email: other?.email ?? null,
      age: other?.age ?? computeAge(other?.dateOfBirth || other?.dob) ?? null,
      height: other?.height ?? toHeightLabel(other?.heightInCm || other?.heightCm) ?? null,
      occupation: other?.occupation ?? other?.profession ?? other?.designation ?? null,
      employerName: other?.employerName ?? null,
      city: other?.city ?? other?.nativePlace ?? null,
      community: other?.community ?? other?.casteDetails ?? null,
      connectedSince:
        (dto as any)?.connectedAt ??
        (dto as any)?.acceptedAt ??
        (dto as any)?.requestedAt ??
        null,
      verified: other?.verified ?? null,
      recentlyActive: other?.recentlyActive ?? null,
    },
  };
};

export function countRecentlyActive(items: MatrimonyConnection[]): number {
  return items.reduce((acc, c) => acc + (c.profile.recentlyActive ? 1 : 0), 0);
}
